import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import api from '../utils/api';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Container,
  Link as MuiLink,
} from '@mui/material';
import {
  People as PeopleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  EventNote as EventNoteIcon,
  CreditCard as CreditCardIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentInteractions, setRecentInteractions] = useState([]);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalInteractions: 0,
    phoneInteractions: 0,
    emailInteractions: 0,
    successfulSales: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upcomingInteractions, setUpcomingInteractions] = useState([]);

  // 데이터 불러오기
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // 고객 목록 전체 불러오기
        const customersRes = await api.get('/customers');
        const customers = customersRes.data;
        
        // 최근 고객 10명 정렬
        const sortedCustomers = [...customers].sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        }).slice(0, 5);
        
        setRecentCustomers(sortedCustomers);
        
        // 최근 10개 상호작용 불러오기 (임시로 첫 고객들의 상호작용만 불러옴)
        // 실제 구현시에는 서버에서 최근 상호작용을 불러오는 API 추가 필요
        let allInteractions = [];
        
        for (let i = 0; i < Math.min(5, customers.length); i++) {
          try {
            const intRes = await api.get(`/interactions/customer/${customers[i].id}`);
            if (intRes.data.length > 0) {
              allInteractions = [...allInteractions, ...intRes.data];
            }
          } catch (err) {
            console.error(`Failed to fetch interactions for customer ${customers[i].id}`);
          }
        }
        
        // 최근 상호작용 정렬
        const sortedInteractions = allInteractions.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        }).slice(0, 10);
        
        setRecentInteractions(sortedInteractions);
        
        // 통계 집계
        const interactionTypes = allInteractions.reduce((acc, curr) => {
          if (curr.type === 'PHONE') acc.phone++;
          if (curr.type === 'EMAIL') acc.email++;
          if (curr.sale_result) acc.sales++;
          return acc;
        }, { phone: 0, email: 0, sales: 0 });
        
        setStats({
          totalCustomers: customers.length,
          totalInteractions: allInteractions.length,
          phoneInteractions: interactionTypes.phone,
          emailInteractions: interactionTypes.email,
          successfulSales: interactionTypes.sales,
        });
        
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || '데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // 예정된 상호작용 목록 가져오기
  const fetchUpcomingInteractions = async () => {
    try {
      const response = await api.get('/interactions/upcoming');
      setUpcomingInteractions(response.data.slice(0, 5)); // 최근 5개만 표시
    } catch (error) {
      console.error('예정된 상호작용 가져오기 실패:', error);
    }
  };

  useEffect(() => {
    fetchUpcomingInteractions();
  }, []);

  // 상호작용 타입에 따른 색상
  const getTypeColor = (type) => {
    switch (type) {
      case 'PHONE':
        return 'primary';
      case 'EMAIL':
        return 'info';
      case 'MEETING':
        return 'success';
      default:
        return 'default';
    }
  };

  // 상호작용 타입에 따른 한글 라벨
  const getTypeLabel = (type) => {
    switch (type) {
      case 'PHONE':
        return '전화';
      case 'EMAIL':
        return '이메일';
      case 'MEETING':
        return '미팅';
      default:
        return '기타';
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1">
          대시보드
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          환영합니다, {user?.name || '사용자'}님!
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <PeopleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="div">{stats.totalCustomers}</Typography>
            <Typography variant="body2" color="text.secondary">총 고객 수</Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <EventNoteIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="div">{stats.totalInteractions}</Typography>
            <Typography variant="body2" color="text.secondary">총 상호작용 수</Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CreditCardIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" component="div" color="success.main">{stats.successfulSales}</Typography>
            <Typography variant="body2" color="text.secondary">성공한 판매</Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <PhoneIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="div">{stats.phoneInteractions}</Typography>
            <Typography variant="body2" color="text.secondary">전화 상호작용</Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <EmailIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
            <Typography variant="h4" component="div">{stats.emailInteractions}</Typography>
            <Typography variant="body2" color="text.secondary">이메일 상호작용</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* 최근 추가된 고객 및 최근 상호작용 */}
      <Grid container spacing={3}>
        {/* 최근 추가된 고객 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">최근 추가된 고객</Typography>
              <Button
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/customers')}
              >
                모든 고객
              </Button>
            </Box>
            
            {recentCustomers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  아직 고객이 없습니다.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/customers')}
                  sx={{ mt: 2 }}
                >
                  고객 추가하기
                </Button>
              </Box>
            ) : (
              <List>
                {recentCustomers.map((customer) => (
                  <React.Fragment key={customer.id}>
                    <ListItem
                      button
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <ListItemText
                        primary={customer.name}
                        secondary={
                          <>
                            {customer.organization && `${customer.organization}`}
                            {customer.organization && customer.position && ` · `}
                            {customer.position && `${customer.position}`}
                          </>
                        }
                      />
                      <IconButton edge="end">
                        <ArrowForwardIcon />
                      </IconButton>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* 최근 상호작용 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">최근 상호작용</Typography>
            </Box>
            
            {recentInteractions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  아직 기록된 상호작용이 없습니다.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/customers')}
                  sx={{ mt: 2 }}
                >
                  고객과 상호작용 하기
                </Button>
              </Box>
            ) : (
              <List>
                {recentInteractions.map((interaction) => (
                  <React.Fragment key={interaction.id}>
                    <ListItem
                      button
                      onClick={() => navigate(`/customers/${interaction.customer_id}`)}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                              label={getTypeLabel(interaction.type)}
                              color={getTypeColor(interaction.type)}
                              size="small"
                              sx={{ mr: 1 }}
                            />
                            {interaction.sale_result && (
                              <Chip
                                label="판매 성공"
                                color="success"
                                size="small"
                                sx={{ mr: 1 }}
                              />
                            )}
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(interaction.date)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          interaction.notes
                            ? interaction.notes.length > 50
                              ? `${interaction.notes.substring(0, 50)}...`
                              : interaction.notes
                            : '(내용 없음)'
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 예정된 상호작용 섹션 */}
      <Box mt={4}>
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: 300, // 높이 증가
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              예정된 일정
            </Typography>
            <Button 
              component={RouterLink} 
              to="/calendar" 
              variant="outlined" 
              size="small" 
              startIcon={<CalendarMonthIcon />}
            >
              일정 관리
            </Button>
          </Box>
          <Box sx={{ 
            overflow: 'auto', 
            maxHeight: 220, // 최대 높이 설정하여 3개 정도의 항목이 보이도록 함
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            }
          }}>
            <List>
              {upcomingInteractions.length > 0 ? (
                upcomingInteractions.map((interaction, index) => (
                  <React.Fragment key={interaction.id}>
                    <ListItem 
                      component={RouterLink} 
                      to={`/customers/${interaction.customer_id}`}
                      sx={{ 
                        textDecoration: 'none', 
                        color: 'inherit',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                    >
                      <ListItemText
                        primary={`${interaction.customer_name} - ${getTypeLabel(interaction.type)}`}
                        secondary={`${formatDate(interaction.date)} - ${interaction.notes?.substring(0, 40) || '메모 없음'}${interaction.notes?.length > 40 ? '...' : ''}`}
                      />
                    </ListItem>
                    {index < upcomingInteractions.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  예정된 일정이 없습니다.
                </Typography>
              )}
            </List>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Dashboard; 